import Container from "@mui/material/Container";
import Button from "@mui/material/Button";

import { H4, Span } from "components/Typography";
import { FlexBetween, FlexBox, FlexRow } from "components/flex-box";
import { currentDateTime } from "common";
import modules from "modules";
import { componentRows } from "modules/pcbuilder/constants";
import RowComponent from "./RowComponent";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import { useAppDispatch } from "hooks/use-app-dispatch";
import { useAppSelector } from "hooks/use-app-selector";
import { useIsMobile } from "hooks";
import RowTotal from "./RowTotal";
import RowLabel from "./RowLabel";
import { CameraAlt } from "@mui/icons-material";
import html2canvas from "html2canvas";
import { useSnackBar } from "hooks";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { userBuildFlag } from "common/constants/feature-toggles";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BuilderToolbar from "./BuilderToolbar";
import { useLoginStatus } from "hooks/useLoginStatus";
import { iconStyle } from "components/mobile-navigation/styles";
import { MobileBuildDrawer } from "./MobileBuildDrawer";
import { DialugoeDecision } from "components/dialog/DialogueDecision";
import { useCart } from "hooks/use-cart";
import { useRouter } from "next/router";
import BackspaceOutlinedIcon from "@mui/icons-material/BackspaceOutlined";
import { Grid } from "@mui/material";
import {
  BuildDetailsWithProducts,
  UpdateBuildDetailsPayload,
} from "modules/my-builds/types";
import { SaveBuildDialog } from "./SaveBuildDialog";
import { navigationRoutes } from "common/constants/navigation-routes";
import { useBranchSelection } from "hooks/use-branch-selection";
import { ScreenshotComponent } from "./ScreenshotComponent";

const { actions: pcBuilderActions, selectors: pcBuilderSelectors } =
  modules.pcBuilder;

const { actions: myBuildsActions } = modules.myBuilds;

type Props = {
  configureBuild?: boolean;
  buildDetails?: BuildDetailsWithProducts;
};

const initialBuildDetails = {
  build_name: "Name this build",
  build_id: "",
};

const PCBuilderForm: FC<Props> = ({
  configureBuild = false,
  buildDetails = initialBuildDetails,
}) => {
  const { push } = useRouter();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const snackBar = useSnackBar();
  const snackbar = useSnackBar();

  const { selectedBranchId } = useBranchSelection();
  const { addBuildPartsToCart } = useCart();
  const { isUserLogin } = useLoginStatus();

  const screenShotRef = useRef(null);

  const isLogin = isUserLogin;

  const [isBuildIncomplete, setIsBuildIncomplete] = useState<boolean>(true);
  const [isBuildDrawerOpen, setIsBuildDrawerOpen] = useState<boolean>(false);
  const [successModalOpen, setSucessModalOpen] = useState<boolean>(false);
  const [isDecision, setDecision] = useState<boolean>(false);

  const saveUpdateString = useMemo(
    () => (configureBuild ? "Update Build" : "Save Build"),
    [configureBuild]
  );

  const handleRedirectToBuildList = () => {
    setSucessModalOpen(false);
    push(navigationRoutes.myBuilds);
  };

  const handleSaveBuild = async () => {
    let productList = [];

    buildParts.forEach((components) => {
      // Special case for products like hdd and ssd
      if (components.multipleParts) {
        const memoryProductList = components.multipleParts;
        memoryProductList.forEach((product) => {
          productList.push({
            product_slug: product.product_id,
            quantity: product.quantity,
          });
        });
      } else {
        productList.push({
          product_slug: components.product_id,
          quantity: components.quantity,
        });
      }
    });

    // Generate temp build no. via timestamp
    const tempBuildNo = () => {
      const date = Date.now().toString().split("");
      return date.slice(date.length - 4).join("");
    };

    const requestParams: UpdateBuildDetailsPayload = {
      name: buildDetails?.build_name ?? `Build no.${tempBuildNo()}`,
      products: productList,
      build_id: buildDetails?.build_id ?? null,
    };

    const response: any = configureBuild
      ? await dispatch(myBuildsActions.updateBuildDetailsThunk(requestParams))
      : await dispatch(myBuildsActions.saveBuildThunk(requestParams));

    if (response?.payload?.success) {
      setSucessModalOpen(true);
      dispatch(myBuildsActions.getBuildListThunk({}));

      // Set buildId if build is new
      const uuid =
        (response?.payload?.originalData?.data?.uuid as string) || "";
      uuid ? dispatch(myBuildsActions.setConfigureBuildId(uuid)) : null;
    } else {
      // May improve error handling based on updated api responses in the future
      const responseError =
        response?.payload?.errors?.name?.[0] ?? "Failed to save build";

      snackbar.show({
        severity: "error",
        message: responseError,
      });
    }
  };

  const toggleDecision = () => {
    setDecision(!isDecision);
  };

  const onClickAddToCart = () => {
    if (isLogin) {
      //clear cart first before adding item
      addBuildPartsToCart(navigationRoutes.userCart);
      setDecision(!isDecision);
    }
  };

  const handleBuildDrawerClose = () => {
    setIsBuildDrawerOpen(false);
  };

  const handleBuildDrawerOpen = () => {
    setIsBuildDrawerOpen(true);
  };

  const isEmptyBuildParts = useAppSelector(
    pcBuilderSelectors.selectIsEmptyBuildParts
  );

  const buildParts = useAppSelector(pcBuilderSelectors.selectBuildParts);

  const onClickClearAll = () => {
    dispatch(pcBuilderActions.clearBuildParts());
  };

  // deprecated but retain for now
  const onScreenShot = async () => {
    const documentQuotation: any = document.querySelector("#pc-builds");
    html2canvas(documentQuotation).then((canvas) =>
      canvas.toBlob(
        (blob) =>
          navigator.clipboard
            // @ts-ignore
            // eslint-disable-next-line no-undef
            .write([new ClipboardItem({ "image/png": blob })])
            .then(() =>
              snackBar.show({
                severity: "success",
                message: "Image copied to clipboard",
              })
            )
            .catch(),
        "image/png",
        99
      )
    );
  };

  // deprecated but retain for now
  const onScreenShotMobile = () => {
    const documentQuotation: any = document.querySelector("#pc-builds");
    html2canvas(documentQuotation).then((canvas) => {
      canvas.toBlob(
        (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "PCbuilder_PC_Worth.png";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        },
        "image/png",
        99
      );
    });
  };

  const onScreenShotV2 = async () => {
    if (!screenShotRef.current || !buildParts?.[0]) return;

    const canvas = await html2canvas(screenShotRef.current);
    const dataUrl = canvas.toDataURL("image/png", 1.0);

    const timestamp = Date.now();
    const link = document.createElement("a");
    link.download = `diy_pc_builder_${String(timestamp)}.png`;
    link.href = dataUrl;
    link.click();
  };

  useEffect(() => {
    const buildRequirements = ["CPU", "Motherboard", "RAM", "Casing"];

    const isPresent = (i: String) =>
      buildParts.find((parts) => parts.category_name === i);

    const isStoragePresent = buildParts.find(
      (part) => part.category_name === "SSD" || part.category_name === "HDD"
    );

    if (buildRequirements.every(isPresent) && isStoragePresent) {
      setIsBuildIncomplete(false);
    } else {
      setIsBuildIncomplete(true);
    }
  }, [buildParts]);

  if (isMobile) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <FlexBetween>
          <H4 color="color.grey900" fontWeight={700} fontSize={10}>
            {"BUILD DATE & TIME: "}
            <Span color="color.grey900" fontWeight={500}>
              {currentDateTime()}
            </Span>
          </H4>
          <Button
            sx={{
              height: 20,
              fontSize: 12,
            }}
            onClick={handleBuildDrawerOpen}
          >
            <MoreVertIcon sx={iconStyle} />
          </Button>
        </FlexBetween>
        <FlexBox flexDirection="column" mt={2} mb={4} bgcolor="white">
          <div id="pc-builds">
            {componentRows?.map((part, i) => (
              <RowComponent data={part} key={`${part?.name}${i}`} />
            ))}
          </div>
          <RowTotal toggleDecision={toggleDecision} />
        </FlexBox>
        <FlexRow alignItems={"stretch"} justifyContent={"center"}>
          {userBuildFlag && isLogin ? (
            <Button
              variant="outlined"
              color="secondary"
              disabled={isBuildIncomplete}
              startIcon={<BookmarkBorderIcon />}
              onClick={handleSaveBuild}
            >
              {saveUpdateString}
            </Button>
          ) : null}
          <Button
            disabled={!buildParts?.[0]}
            variant="outlined"
            color="secondary"
            startIcon={<CameraAlt />}
            onClick={onScreenShotV2}
            sx={{
              marginLeft: 4,
              fontSize: 12,
            }}
          >
            Take a Screenshot
          </Button>
          {isEmptyBuildParts ? null : (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<BackspaceOutlinedIcon />}
              sx={{
                marginLeft: 4,
                fontSize: 12,
              }}
              onClick={onClickClearAll}
            >
              Clear Build
            </Button>
          )}
        </FlexRow>
        <DialugoeDecision
          toggleOpen={isDecision}
          dialogueTitle={"DIY Builder"}
          dialogueContent={
            "Adding DIY components to cart requires to remove all current item(s) on your cart, Are you sure you want to add DIY components in your cart?"
          }
          buttonTitle={"Proceed"}
          toggleOpenClose={toggleDecision}
          setFunction={onClickAddToCart}
        />
        {isMobile ? (
          <MobileBuildDrawer
            isBuildDrawerOpen={isBuildDrawerOpen}
            handleBuildDrawerClose={handleBuildDrawerClose}
            onScreenShotMobile={onScreenShotV2}
            handleSaveBuild={handleSaveBuild}
            onClickClearAll={onClickClearAll}
            isBuildIncomplete={isBuildIncomplete}
            toggleDecision={toggleDecision}
            saveString={saveUpdateString}
          />
        ) : null}
        <SaveBuildDialog
          open={successModalOpen}
          closeUploadDialog={handleRedirectToBuildList}
        />
        <ScreenshotComponent screenshotRef={screenShotRef} />
      </Container>
    );
  }

  return (
    <>
      <Grid container padding={3} height={"100%"}>
        <Grid item md={9.5}>
          <H4 color="color.grey900" fontWeight={700} mb={1}>
            {"BUILD DATE & TIME: "}
            <Span color="color.grey900" fontWeight={500}>
              {currentDateTime()}
            </Span>
          </H4>
          <FlexBox
            flexDirection="column"
            mb={1}
            bgcolor="white"
            sx={{ borderRadius: 4 }}
          >
            <>
              <RowLabel />
              <div id="pc-builds">
                {componentRows?.map((part, i) => (
                  <RowComponent data={part} key={`${part?.name}${i}`} />
                ))}
              </div>
              <RowTotal />
            </>
          </FlexBox>
        </Grid>
        <Grid item md>
          <BuilderToolbar
            isBuildIncomplete={isBuildIncomplete}
            openUploadDialog={handleSaveBuild}
            onScreenShot={onScreenShotV2}
            saveString={saveUpdateString}
          />
        </Grid>
      </Grid>
      <SaveBuildDialog
        open={successModalOpen}
        closeUploadDialog={handleRedirectToBuildList}
      />
      <ScreenshotComponent screenshotRef={screenShotRef} />
    </>
  );
};

export default PCBuilderForm;
